import { filterClasses } from "@swivl-mono/helpers";
import { BotSequenceConfigStep, BotSequenceType } from "@swivl-mono/types";
import { Switch } from "@swivl-mono/ui";
import { BiChevronDown, BiChevronUp, BiX } from "react-icons/bi";

import { useSequencesV3Context } from "../SequencesV3.Context";
import { summarizeDay } from "../ladderDraft";
import { dayCaption } from "../sequenceTypeMeta";

interface DayRowProps {
  sequenceType: BotSequenceType;
  step: BotSequenceConfigStep;
  expanded: boolean;
  /** Read-only rows (inherited ladder, or no edit permission) show no toggle, remove or edit affordances. */
  readOnly: boolean;
  /** The day's number is fixed once saved (decision 37); only an unsaved day may be renumbered. */
  numberEditable: boolean;
  onToggleExpanded: () => void;
  onEnabledChange: (enabled: boolean) => void;
  onNumberChange: (offsetDays: number) => void;
  onRemove: () => void;
  children?: React.ReactNode;
}

/** One day of the sequence: Day N + caption, on/off, what it does, expand for its settings. */
export function DayRow({
  sequenceType,
  step,
  expanded,
  readOnly,
  numberEditable,
  onToggleExpanded,
  onEnabledChange,
  onNumberChange,
  onRemove,
  children
}: DayRowProps): React.ReactElement {
  const { scripts } = useSequencesV3Context().scripts;
  const chips = summarizeDay(step, sequenceType, scripts.find((script) => script.slug === step.callScriptSlug)?.name);

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const parsed = Number.parseInt(event.target.value, 10);
    if (!Number.isNaN(parsed) && parsed >= 0) onNumberChange(parsed);
  };

  const handleRemove = (): void => {
    if (!window.confirm(`Remove Day ${step.offsetDays}? Tenants already past this day are not affected.`)) return;
    onRemove();
  };

  return (
    <div className={filterClasses("border-b border-grey-100 last:border-b-0", expanded && "bg-grey-50")}>
      <div className={filterClasses("flex items-center justify-between gap-4 px-4 py-3", !step.enabled && "text-grey-600")}>
        <div className="flex min-w-0 items-center gap-4">
          {readOnly ? (
            <Switch on={step.enabled} readOnly activeColor="orange" />
          ) : (
            <Switch on={step.enabled} onSet={onEnabledChange} activeColor="orange" />
          )}
          <div className="min-w-0">
            <div className="body-sm-alt flex items-center gap-2 text-grey-900">
              {numberEditable && !readOnly ? (
                <span className="flex items-center gap-1">
                  Day
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={step.offsetDays}
                    onChange={handleNumberChange}
                    className="body-sm w-16 rounded-sm border border-grey-400 px-2 py-0.5 text-center"
                    aria-label="Day number"
                  />
                </span>
              ) : (
                <span>Day {step.offsetDays}</span>
              )}
              <span className="body-sm font-normal text-grey-600">· {dayCaption(sequenceType, step.offsetDays)}</span>
            </div>
            {chips.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {chips.map((chip) => (
                  <span key={chip} className="body-xs rounded-xs border border-grey-200 bg-grey-50 px-1.5 py-0.5 text-grey-700">
                    {chip}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3 text-grey-600">
          <button type="button" onClick={onToggleExpanded} className="hover:text-grey-900" aria-label={expanded ? "Collapse day" : "Expand day"}>
            {expanded ? <BiChevronUp className="size-5" /> : <BiChevronDown className="size-5" />}
          </button>
          {!readOnly && (
            <button type="button" onClick={handleRemove} className="hover:text-red-600" aria-label="Remove day">
              <BiX className="size-5" />
            </button>
          )}
        </div>
      </div>
      {expanded && <div className="px-4 pb-4 pl-[82px]">{children}</div>}
    </div>
  );
}
